'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import CircularPercentage from '@/comps/CircularPercentage';
import { Button } from '@/comps/primitives/button';
import { CreateProgramButton } from '@/domains/pages/ai-proficiency/components/create-program-button';
import { DimensionRadarChart } from '@/domains/pages/ai-proficiency/components/dimension-radar-chart';
import { LevelShareDonut } from '@/domains/pages/ai-proficiency/components/level-share-donut';
import { AIFI_LEVEL_COLORS, METHODOLOGY_DIMENSIONS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { getDimensionTooltip } from '@/domains/pages/ai-proficiency/copy/dimension-tooltips';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { LevelDimensionTargets } from '@/domains/pages/ai-proficiency/fixtures/upskilling-benchmarks';
import type { MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import type { LevelSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { rankDimensionsByLowestScore } from '@/domains/pages/ai-proficiency/utils/upskilling';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ArrowRight, Info, Users } from 'lucide-react';
import { useMemo } from 'react';

/** Weakest dimensions surfaced in the "Where <level>s lack" panel. */
const WEAKEST_DIMENSION_COUNT = 2;
type UpskillingLevelCardProps = {
  level: AifiLevel;
  snapshot: LevelSnapshot | undefined;
  groupDimensions: MethodologyDimension[];
  orgDimensions: MethodologyDimension[];
  levelTargets: LevelDimensionTargets;
  scopeLabel: string;
  onViewDetails: () => void;
  onCreateProgram: (dimensionKey: string, dimensionLabel: string) => void;
  suppressRadarTooltip?: boolean;
  className?: string;
};
export function UpskillingLevelCard(t0) {
  const $ = _c(70);
  const {
    level,
    snapshot,
    groupDimensions,
    orgDimensions,
    levelTargets,
    scopeLabel,
    onViewDetails,
    onCreateProgram,
    suppressRadarTooltip: t1,
    className
  } = t0;
  const suppressRadarTooltip = t1 === undefined ? false : t1;
  const color = AIFI_LEVEL_COLORS[level];
  const userCount = snapshot?.userCount ?? 0;
  const sharePercent = snapshot?.sharePercent ?? 0;
  let t2;
  if ($[0] !== groupDimensions) {
    t2 = rankDimensionsByLowestScore(groupDimensions).slice(0, WEAKEST_DIMENSION_COUNT).map(_temp);
    $[0] = groupDimensions;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const weakestDimensions = t2;
  const topRecommendation = weakestDimensions[0] ?? null;
  let t3;
  if ($[2] !== className) {
    t3 = cn("flex h-full min-w-0 flex-col", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `upskilling-level-card-${level}`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("line-clamp-1 shrink-0 text-[13px] tracking-[0.18em] uppercase", AIFI_DATA_NUMBER_CLASS);
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== color) {
    t6 = {
      color
    };
    $[5] = color;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== level || $[8] !== t6) {
    t7 = <h3 className={t5} style={t6}>{level}</h3>;
    $[7] = level;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = `the ${scopeLabel}`;
  let t9;
  if ($[10] !== color || $[11] !== level || $[12] !== sharePercent || $[13] !== t8 || $[14] !== userCount) {
    t9 = <LevelShareDonut sharePercent={sharePercent} color={color} label={level} userCount={userCount} shareOf={t8} sizePx={24} />;
    $[10] = color;
    $[11] = level;
    $[12] = sharePercent;
    $[13] = t8;
    $[14] = userCount;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== sharePercent) {
    t10 = formatScore(sharePercent, {
      decimals: 1
    });
    $[16] = sharePercent;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <span className={AIFI_DATA_NUMBER_CLASS}>{t10}%</span>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== scopeLabel || $[21] !== t11) {
    t12 = <span className="min-w-0">{t11} of the{" "}{scopeLabel}</span>;
    $[20] = scopeLabel;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span aria-hidden={true}>·</span>;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Users className="size-3.5 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== userCount) {
    t15 = formatUserCount(userCount);
    $[25] = userCount;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15) {
    t16 = <span className="flex shrink-0 items-center gap-1.5">{t14}<span className={AIFI_DATA_NUMBER_CLASS}>{t15}</span></span>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t12 || $[30] !== t16 || $[31] !== t9) {
    t17 = <span className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">{t9}{t12}{t13}{t16}</span>;
    $[29] = t12;
    $[30] = t16;
    $[31] = t9;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17 || $[34] !== t7) {
    t18 = <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">{t7}{t17}</div>;
    $[33] = t17;
    $[34] = t7;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArrowRight className="size-3" />;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== onViewDetails) {
    t20 = <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1 rounded-full px-3 text-xs" onClick={onViewDetails}>View details{t19}</Button>;
    $[37] = onViewDetails;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t20) {
    t21 = <div className="flex shrink-0 items-center justify-between gap-2 border-b border-b-lightborder bg-background px-8 py-2">{t18}{t20}</div>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== color || $[43] !== groupDimensions || $[44] !== level || $[45] !== levelTargets || $[46] !== orgDimensions || $[47] !== suppressRadarTooltip) {
    t22 = <DimensionRadarChart level={level} levelColor={color} groupDimensions={groupDimensions} orgDimensions={orgDimensions} levelTargets={levelTargets} suppressTooltip={suppressRadarTooltip} compact={true} className="w-52 shrink-0" />;
    $[42] = color;
    $[43] = groupDimensions;
    $[44] = level;
    $[45] = levelTargets;
    $[46] = orgDimensions;
    $[47] = suppressRadarTooltip;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== level) {
    t23 = <p className="text-[11px] font-medium tracking-[0.14em] text-muted-foreground/70 uppercase">Where {level}s lack</p>;
    $[49] = level;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== color || $[52] !== weakestDimensions) {
    t24 = weakestDimensions.length ? <ul className="mt-2.5 flex flex-col gap-2.5">{weakestDimensions.map(dimension_0 => <li key={dimension_0.key} className="flex min-w-0 flex-col"><div className="flex min-w-0 items-center"><p className="flex min-w-0 items-center gap-1.5 text-sm leading-snug font-medium text-foreground"><span className="min-w-0">{dimension_0.label}</span><CircularPercentage percentage={Math.min(1, Math.max(0, (dimension_0.score ?? 0) / 100))} radius={11} stroke={2.5} colorHex={color} hideLabel={true} className="shrink-0" /><span className={cn("-ml-1 shrink-0 text-xs font-normal text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}>{formatScore(dimension_0.score, {
                decimals: 0
              })}/100</span></p></div><p className="text-xs leading-snug text-muted-foreground">{dimension_0.description}{dimension_0.tooltip ? <BasicTooltip text={dimension_0.tooltip} side="bottom" classNameContent="max-w-80 break-normal leading-relaxed"><button type="button" aria-label={`About ${dimension_0.label.toLowerCase()}`} className="ml-1 inline-flex cursor-default align-[-0.1em] text-muted-foreground outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"><Info className="size-3" aria-hidden={true} /></button></BasicTooltip> : null}</p></li>)}</ul> : <p className="mt-2.5 text-xs leading-snug text-muted-foreground">No dimension data available for this level.</p>;
    $[51] = color;
    $[52] = weakestDimensions;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t23 || $[55] !== t24) {
    t25 = <div className="flex min-w-0 flex-col">{t23}{t24}</div>;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t22 || $[58] !== t25) {
    t26 = <div className="grid min-w-0 grow grid-cols-[auto_minmax(0,1fr)] items-center gap-6 px-8 py-2">{t22}{t25}</div>;
    $[57] = t22;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== color || $[61] !== onCreateProgram || $[62] !== topRecommendation) {
    t27 = topRecommendation ? <div className="min-w-0 shrink-0 px-8 pb-3"><div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5" style={{
        borderColor: `${color}59`,
        backgroundColor: `${color}0D`
      }}><div className="min-w-0"><p className="text-sm leading-snug font-semibold text-foreground">Improve {topRecommendation.label.toLowerCase()}</p><p className="mt-0.5 text-xs leading-snug text-muted-foreground">{topRecommendation.coaching}</p></div><CreateProgramButton size="compact" className="shrink-0" onClick={() => onCreateProgram(topRecommendation.key, topRecommendation.label)} /></div></div> : null;
    $[60] = color;
    $[61] = onCreateProgram;
    $[62] = topRecommendation;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t21 || $[65] !== t26 || $[66] !== t27 || $[67] !== t3 || $[68] !== t4) {
    t28 = <div className={t3} data-testid={t4}>{t21}{t26}{t27}</div>;
    $[64] = t21;
    $[65] = t26;
    $[66] = t27;
    $[67] = t3;
    $[68] = t4;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  return t28;
}
function _temp(dimension) {
  const meta = METHODOLOGY_DIMENSIONS.find(item => item.key === dimension.key);
  return {
    key: dimension.key,
    label: meta?.label ?? dimension.label,
    description: meta?.description ?? dimension.description,
    tooltip: getDimensionTooltip(dimension.key, meta?.description ?? dimension.description),
    coaching: meta?.coaching ?? dimension.description,
    score: dimension.score
  };
}